import { useLayoutEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useMotionValueEvent,
  useTransform,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import { mediaUrl } from "@/lib/mediaUrl";
import { getWhatsAppUrl } from "@/lib/contact";
import { contactSection } from "@/i18n/contactSection";
import { ArrowUpRight } from "lucide-react";

const SERVICE_MEDIA = [
  { video: mediaUrl("/services/websites-systems.mp4"), poster: mediaUrl("/services/websites-poster.webp"), logo: mediaUrl("/services/react-logo.png") },
  { image: mediaUrl("/services/ai-systems.webp"), logo: mediaUrl("/services/claude-logo.png") },
  { image: mediaUrl("/services/saas-explainers.webp"), logo: mediaUrl("/services/after-effects-logo.png") },
];

const ServiceMedia = ({ media, active }) => {
  const videoRef = useRef(null);
  const reduce = useReducedMotion();

  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || reduce || !active) {
      video?.pause();
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0.2 });
    observer.observe(video);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [active, media.video, reduce]);

  return media.video ? (
    <video ref={videoRef} muted loop playsInline preload="none" poster={media.poster} className="absolute inset-0 h-full w-full object-cover" aria-hidden="true">
      <source src={media.video} type="video/mp4" />
    </video>
  ) : (
    <img src={media.image} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
  );
};

const PreviewContent = ({ service, index }) => {
  const { t, lang } = useLanguage();
  const media = SERVICE_MEDIA[index];
  return (
    <div className="relative z-10 flex h-full flex-col justify-between gap-4 p-5 text-white sm:p-6 md:p-7">
        <div className="flex items-center justify-between">
          <img src={media.logo} alt="" aria-hidden="true" className="size-12 object-contain drop-shadow-[0_3px_8px_rgba(0,0,0,0.35)]" />
          <span className="font-display text-3xl font-bold text-white/70" dir="ltr">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className="text-start drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
          <h3 className="text-xl font-bold leading-tight md:text-2xl">{service.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-white/90">{service.desc}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {service.chips.map((chip) => (
              <span key={chip} className="rounded-full border border-white/25 bg-black/35 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                {chip}
              </span>
            ))}
          </div>
          <a
            href={getWhatsAppUrl(`${contactSection[lang].whatsappMessage}\n${service.title}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full border border-white/50 bg-black/40 px-4 text-sm font-semibold text-white transition-colors hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal"
          >
            {t.build.cta}
            <ArrowUpRight className="size-4 rtl:-scale-x-100" aria-hidden="true" />
          </a>
        </div>
    </div>
  );
};

const PreviewCard = ({ service, index }) => (
  <div className="relative isolate h-full w-full overflow-hidden rounded-[1.75rem] bg-charcoal">
    <ServiceMedia media={SERVICE_MEDIA[index]} active />
    <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
    <PreviewContent service={service} index={index} />
  </div>
);

const ProgressPill = ({ active, total, progress }) => (
  <div
    data-testid="build-progress-indicator"
    dir="ltr"
    className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold tabular-nums text-graphite/50 shadow-card"
  >
    <span className="text-charcoal">{String(active + 1).padStart(2, "0")}</span>
    <span>/</span>
    <span>{String(total).padStart(2, "0")}</span>
    <span aria-hidden="true" className="relative ms-2 h-2 w-20 overflow-hidden rounded-full bg-mist">
      <motion.span
        className="absolute inset-0 origin-left rounded-full bg-sage"
        style={{ scaleX: progress }}
      />
    </span>
  </div>
);

// A single moving window reveals the dark copy of the entire list. Keeping the
// text stationary is what produces the sliced letters between two services.
const ServiceRows = ({ services, active, lang, overlay = false }) => (
  <ul
    data-testid={overlay ? undefined : "build-service-list-window"}
    className={overlay ? "text-charcoal" : "text-[#C9CDC9]"}
  >
    {services.map((service, i) => (
      <li
        key={service.title}
        data-testid={overlay ? undefined : `build-service-item-${i}`}
        data-active={overlay ? undefined : active === i}
        aria-current={!overlay && active === i ? "step" : undefined}
        className="flex items-center gap-4 py-2.5 xl:gap-6 xl:py-3"
      >
        <span className="w-7 shrink-0 text-xs font-semibold tabular-nums" dir="ltr">
          {String(i + 1).padStart(2, "0")}
        </span>
        <span
          className={`text-[clamp(1.8rem,3.4vw,3.7rem)] font-bold leading-[1.12] ${
            lang === "ar" ? "" : "tracking-tight"
          }`}
        >
          {service.title}
        </span>
      </li>
    ))}
  </ul>
);

const PreviewLayer = ({ index, active, position }) => {
  // Reveal only the incoming media; the copy above it always belongs to the active service.
  const reveal = useTransform(position, (value) =>
    index === 0 ? 1 : Math.min(1, Math.max(0, (value - index + 0.65) / 0.5)),
  );
  const clipPath = useTransform(reveal, (value) => `inset(${(1 - value) * 100}% 0 0 0)`);
  const y = useTransform(reveal, [0, 1], ["6%", "0%"]);
  const scale = useTransform(reveal, [0, 1], [1.035, 1]);

  return (
    <motion.div
      aria-hidden="true"
      className="absolute inset-0"
      style={{ clipPath, zIndex: index }}
    >
      <motion.div className="relative h-full w-full" style={{ y, scale }}>
        <ServiceMedia media={SERVICE_MEDIA[index]} active={active === index} />
      </motion.div>
    </motion.div>
  );
};

const DesktopBuild = ({ t }) => {
  const { lang } = useLanguage();
  const services = t.build.services;
  const n = services.length;
  const listRef = useRef(null);
  const geometry = useMotionValue({ rows: [], height: 0 });
  const [active, setActive] = useState(0);

  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start center", "end center"],
  });

  const position = useTransform(() => {
    const progress = scrollYProgress.get();
    const { rows, height } = geometry.get();
    if (!rows.length) return 0;
    const center = progress * height;
    for (let i = 0; i < rows.length - 1; i += 1) {
      const start = rows[i].top + rows[i].height / 2;
      const end = rows[i + 1].top + rows[i + 1].height / 2;
      if (center <= end) return i + Math.max(0, (center - start) / (end - start));
    }
    return rows.length - 1;
  });
  const progress = useTransform(position, [0, Math.max(1, n - 1)], [0, 1]);
  const band = useTransform(() => {
    const { rows, height } = geometry.get();
    if (!rows.length) return { top: 0, bottom: 0, center: 0 };
    const value = position.get();
    const index = Math.floor(value);
    const from = rows[index];
    const to = rows[Math.min(index + 1, rows.length - 1)];
    const fraction = value - index;
    const top = from.top + (to.top - from.top) * fraction;
    const rowHeight = from.height + (to.height - from.height) * fraction;
    return { top, bottom: height - top - rowHeight, center: top + rowHeight / 2 };
  });
  const clipPath = useTransform(band, ({ top, bottom }) => `inset(${top}px 0 ${Math.max(0, bottom)}px 0)`);
  const headingTop = useTransform(band, ({ center }) => center);
  const listHeight = useTransform(geometry, ({ height }) => height);

  useLayoutEffect(() => {
    const list = listRef.current;
    const measure = () => {
      const listBounds = list.getBoundingClientRect();
      // The desktop layout stays mounted but is hidden below the breakpoint.
      if (!listBounds.height) {
        geometry.set({ rows: [], height: 0 });
        return;
      }
      const rows = Array.from(list.firstElementChild.children, (row) => {
        const bounds = row.getBoundingClientRect();
        return { top: bounds.top - listBounds.top, height: bounds.height };
      });
      geometry.set({ rows, height: listBounds.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [geometry, lang, services]);

  useMotionValueEvent(position, "change", (v) => {
    const idx = Math.min(n - 1, Math.max(0, Math.round(v)));
    setActive((prev) => (prev === idx ? prev : idx));
  });

  return (
    <div className="relative pb-28 pt-10 xl:pb-32 xl:pt-12">
        <div className="mx-auto grid w-full max-w-[92rem] grid-cols-[minmax(0,1fr)_minmax(0,24rem)] items-center gap-10 px-8 xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,27rem)] xl:gap-12 xl:px-14">
          <motion.div className="relative hidden xl:block" style={{ height: listHeight }}>
            <span aria-hidden="true" className="invisible whitespace-nowrap font-display text-5xl font-bold xl:text-6xl">
              {t.build.title}
            </span>
            <motion.h2
              data-testid="build-sticky-title"
              className="absolute start-0 whitespace-nowrap font-display text-5xl font-bold text-charcoal xl:text-6xl"
              style={{ top: headingTop, y: "-50%" }}
            >
              {t.build.title}
            </motion.h2>
          </motion.div>

          <div ref={listRef} className="relative">
            <ServiceRows services={services} active={active} lang={lang} />
            <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ clipPath }}>
              <ServiceRows services={services} active={active} lang={lang} overlay />
            </motion.div>
          </div>

          <div className="relative">
            <div
              className="relative min-h-[440px] overflow-hidden rounded-[1.75rem] border-[6px] border-white shadow-soft"
              data-testid="build-preview-panel"
            >
              {services.map((service, index) => (
                <PreviewLayer key={service.title} index={index} active={active} position={position} />
              ))}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
              <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }} className="absolute inset-0 z-20">
                <PreviewContent service={services[active]} index={active} />
              </motion.div>
            </div>
            <div className="absolute -bottom-14 end-0">
              <ProgressPill active={active} total={n} progress={progress} />
            </div>
          </div>
        </div>
    </div>
  );
};

const StackedBuild = ({ t, reduce }) => (
  <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
    <SectionHeading
      eyebrow={t.build.eyebrow}
      title={t.build.title}
      sub={t.build.sub}
      testId="build-heading-mobile"
    />
    <div className="mx-auto mt-12 grid max-w-5xl gap-12 md:grid-cols-2 md:gap-10">
      {t.build.services.map((service, i) => (
        <motion.div
          key={service.title}
          data-testid={`build-service-card-${i}`}
          initial={reduce ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-4 flex items-baseline gap-3">
            <span className="font-display text-sm font-bold text-sage-deep" dir="ltr">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="text-lg font-bold text-charcoal">{service.title}</h3>
          </div>
          <div className="h-[440px]">
            <PreviewCard service={service} index={i} />
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const WhatIBuild = () => {
  const { t } = useLanguage();
  const reduce = useReducedMotion();

  return (
    <section id="build" data-testid="what-i-build-section" className="relative z-10 -mt-8 scroll-mt-28 rounded-t-[2rem] bg-softwhite shadow-[0_-24px_55px_-48px_rgba(11,11,11,0.18)] md:rounded-t-[3rem]">
      {!reduce && <div className="mx-auto hidden max-w-7xl px-8 pt-16 lg:block">
        <SectionHeading eyebrow={t.build.eyebrow} title={t.build.title} sub={t.build.sub} />
      </div>}
      <div className="hidden lg:block">
        {reduce ? <StackedBuild t={t} reduce /> : <DesktopBuild t={t} />}
      </div>
      <div className="lg:hidden">
        <StackedBuild t={t} reduce={reduce} />
      </div>
    </section>
  );
};

export default WhatIBuild;
